import { Component, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormsModule } from '@angular/forms';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { LayerType, SubLayerType } from '../types';


export interface TreeChild {
	id: number | string;
	label: string;
	checked?: boolean;
	type?: SubLayerType;
}


export interface TreeParent {
	id: number | string;
	label: string;
	type: LayerType;
	checked?: boolean;
	indeterminate?: boolean;
	children?: TreeChild[];
	loading?: boolean; // for async load
}

@Component({
	selector: 'bp-checkbox-tree',
	standalone: true,
	imports: [NgFor, NgIf, CommonModule, FormsModule],
	templateUrl: './checkbox-tree.component.html',
	styleUrls: ['./checkbox-tree.component.css'],
	animations: [
		trigger('expand', [
			state('collapsed', style({ height: '0', opacity: 0 })),
			state('expanded', style({ height: '*', opacity: 1 })),
			transition('collapsed <=> expanded', animate('200ms ease-in-out')),
		])
	]
})
export class CheckboxTreeComponent {
	@Input() nodes: TreeParent[] = [];
	@Input() loadChildren?: (node: TreeParent) => Promise<TreeChild[]>; // optional async loader
	@Input() parentLabelTemplate?: TemplateRef<any>;
	@Input() childLabelTemplate?: TemplateRef<any>;
	@Input() showDelete = false;
	@Input() showStyle = false;  // Zeigt Style-Button für GPKG-Sublayer
	@Input() showZoom = false;   // Zeigt Zoom-Button für GPKG-Sublayer
	@Input() disabled = false;
	@Output() selectionChange = new EventEmitter<{ parents: TreeParent[] }>();
	@Output() deleteNode = new EventEmitter<TreeParent>();
	@Output() styleNode = new EventEmitter<{ parent: TreeParent, child: TreeChild }>();
	@Output() styleParentNode = new EventEmitter<TreeParent>();  // Für GeoJSON ohne Sublayer
	@Output() zoomNode = new EventEmitter<{ parent: TreeParent, child: TreeChild }>();


	parentId(node: TreeParent) {
		return `parent-${node.id}`;
	}

	childId(parent: TreeParent, child: TreeChild) {
		return `child-${parent.id}-${child.id}`;
	}

	async onParentToggle(node: TreeParent) {
		if (node.checked && this.loadChildren && (!node.children || node.children.length === 0)) {
			node.loading = true;
			try {
				node.children = await this.loadChildren(node);
			} catch (err) {
				console.warn(`Sublayer für "${node.label}" konnten nicht geladen werden:`, err);
				node.checked = false;
			} finally {
				node.loading = false;
			}
		} else if (!node.checked) {
			node.children?.forEach(c => c.checked = false);
		}
		this.updateIndeterminate(node);
		this.emitSelection();
	}


	onChildToggle(parent: TreeParent, child: TreeChild) {
		const anyChecked = parent.children?.some(c => c.checked);

		// Parent ist checked wenn mindestens ein Child checked
		parent.checked = !!anyChecked;
		this.updateIndeterminate(parent);

		this.emitSelection();
	}


	updateIndeterminate(node: TreeParent) {
		if (!node.children) return;
		const allChecked = node.children.every(c => c.checked);
		const anyChecked = node.children.some(c => c.checked);

		node.indeterminate = anyChecked && !allChecked;
	}


	private emitSelection() {
		const snapshot = this.nodes.map(p => ({
			...p,
			children: p.children?.map(c => ({ ...c })) || []
		}));
		this.selectionChange.emit({ parents: snapshot });
	}
}
